import React from "react";
import { Document } from "../../common/redux/documentsAction";
import {
    Box,
    Typography,
    IconButton,
    Card,
    CardContent,
    CardActions,
    styled,
    Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LabelChip from "./LabelChip";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";

type Props = {
    document: Document;
    onView?: (doc: Document) => void;
    onRename?: (doc: Document, event: React.MouseEvent) => void;
    onDelete?: (doc: Document, event: React.MouseEvent) => void;
    onDownload?: (doc: Document, event: React.MouseEvent) => void;
};

const CardDocument: React.FC<Props> = ({
    document,
    onView,
    onRename,
    onDelete,
    onDownload,
}) => {
    const { t } = useTranslation();

    return (
        <ContainerCard
            variant="outlined"
            onClick={() => onView?.(document)}
            aria-label={t("tooltip.preview.document", {
                name: document.name,
            })}
            role="button"
            tabIndex={0}
        >
            <CustomCardContent>
                {document.name && (
                    <Box display="flex" justifyContent="flex-end" width="100%">
                        <LabelChip label={document.type} />
                    </Box>
                )}
                <Box display="flex" flexDirection="column" gap={1}>
                    <Typography fontWeight={600}>{document.name}</Typography>
                    {document.created_at && (
                        <Typography variant="body2" color="text.secondary">
                            {t("manageDocuments.uploaded.data")}{" "}
                            {dayjs(document.created_at).format("DD MMMM YYYY")}
                        </Typography>
                    )}
                </Box>
            </CustomCardContent>

            <CardActionsStyled>
                {onRename && (
                    <Tooltip title={t("tooltip.rename")} arrow>
                        <IconButton
                            aria-label="Rename"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRename(document, e);
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {onDownload && (
                    <Tooltip title={t("tooltip.download")} arrow>
                        <IconButton
                            aria-label="Download"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDownload(document, e);
                            }}
                        >
                            <DownloadIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {onDelete && (
                    <Tooltip title={t("tooltip.delete")} arrow>
                        <IconButton
                            aria-label="Delete"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(document, e);
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </CardActionsStyled>
        </ContainerCard>
    );
};

export default CardDocument;

const ContainerCard = styled(Card)(({ theme }) => ({
    width: "320px",
    marginBottom: "1rem",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    borderRadius: "10px",
    backgroundColor: "#F0F4F8",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
        boxShadow: theme.shadows[1],
        transform: "scale(1.01)",
    },
    "&:focus": {
        outline: `1px solid ${theme.palette.primary.main}`,
        outlineOffset: "2px",
    },
}));

const CustomCardContent = styled(CardContent)({
    padding: "0.75rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
});

const CardActionsStyled = styled(CardActions)({
    justifyContent: "flex-end",
    paddingBottom: "0.5rem",
    paddingRight: "0.5rem",
});
